import React, { PureComponent } from 'react';
import { Backdrop, ButtonPrimary, ButtonTertiary, DialogAlert, Type } from '@spotify-internal/encore-web';
import PropTypes from 'prop-types';

import ImageChooser from 'components/ContentReporting/Form/Fields/ImageChooser/ImageChooser';
import withTranslation from 'lib/shared/i18n/withTranslation';
import TextareaField from 'components/Fields/TextareaField/TextareaField';
import SelectField from 'components/Fields/SelectField/SelectField';
import BackButton from 'components/ContentReporting/ContentLegalReporting/BackButton/BackButton';
import analytics, {
  ShowStepEventLabel,
} from 'components/ContentReporting/ContentLegalReporting/report-content-analytics-utils';
import {
  CONTENT_DETAILS,
  getClaimTypeURIComponent,
  OPTIONS_CLAIM_TYPE,
  OPTIONS_CLAIM_TYPE_LIST,
  withFullTranslationKey,
} from 'components/ContentReporting/ContentLegalReporting/content-legal-reporting-config';
import {
  ASSOCIATED_CONTENT_ERRORS,
  ASSOCIATED_CONTENT_IMAGE_TYPES,
  ASSOCIATED_CONTENT_TYPES,
  OPTIONS_CONTENT_TYPE,
  USE_LABEL_FROM_TRANSLATION_PREFIX,
} from 'components/ContentReporting/shared-constants';
import { buildProductUrisList, getFieldValid, getFieldValue } from 'components/ContentReporting/form-utils';
import {
  convertPlaylistV1ToV2,
  getValidSpotifyUriList,
  isValidSpotifyUriList,
} from 'components/ContentReporting/validation-utils';
import { withContextualInfo } from 'components/ContentReporting/withContextualInfo';

import { fetchResolveUris } from '../../api-helper';

export const visibility = {
  hidden: 'hidden',
  pristine: 'pristine',
  touched: 'touched',
};

const getContentTypeOptions = claimType => {
  switch (claimType) {
    case OPTIONS_CLAIM_TYPE.TRADEMARK:
    case OPTIONS_CLAIM_TYPE.COPYRIGHT:
    case OPTIONS_CLAIM_TYPE.OTHER:
      return [
        OPTIONS_CONTENT_TYPE.MUSIC,
        OPTIONS_CONTENT_TYPE.PODCAST,
        OPTIONS_CONTENT_TYPE.AUDIOBOOK,
        OPTIONS_CONTENT_TYPE.ARTWORK,
        OPTIONS_CONTENT_TYPE.USER_IMAGE,
        OPTIONS_CONTENT_TYPE.PLAYLIST_IMAGE,
        OPTIONS_CONTENT_TYPE.PLAYLIST_TITLE,
        OPTIONS_CONTENT_TYPE.PLAYLIST_DESCRIPTION,
        OPTIONS_CONTENT_TYPE.ARTIST_PROFILE_IMAGE,
        OPTIONS_CONTENT_TYPE.ARTIST_BANNER_IMAGE,
        OPTIONS_CONTENT_TYPE.ARTIST_GALLERY_IMAGE,
        OPTIONS_CONTENT_TYPE.ARTIST_BIO_TEXT,
        OPTIONS_CONTENT_TYPE.ARTIST_PICK_TEXT,
        OPTIONS_CONTENT_TYPE.ARTIST_MERCHANDISE,
        OPTIONS_CONTENT_TYPE.NOW_PLAYING,
        OPTIONS_CONTENT_TYPE.ARTIST_VIDEO_VIDEO,
        OPTIONS_CONTENT_TYPE.ARTIST_VIDEO_TITLE,
        OPTIONS_CONTENT_TYPE.ARTIST_VIDEO_AUDIO,
        OPTIONS_CONTENT_TYPE.POLL_QUESTION_TEXT,
        OPTIONS_CONTENT_TYPE.POLL_OPTIONS_TEXT,
        OPTIONS_CONTENT_TYPE.RESPONSE_TEXT,
        OPTIONS_CONTENT_TYPE.QUESTION_TEXT,
      ];
    case OPTIONS_CLAIM_TYPE.PERSONAL:
      return [
        OPTIONS_CONTENT_TYPE.ARTWORK,
        OPTIONS_CONTENT_TYPE.USER_IMAGE,
        OPTIONS_CONTENT_TYPE.PLAYLIST_IMAGE,
        OPTIONS_CONTENT_TYPE.PLAYLIST_TITLE,
        OPTIONS_CONTENT_TYPE.PLAYLIST_DESCRIPTION,
        OPTIONS_CONTENT_TYPE.ARTIST_PROFILE_IMAGE,
        OPTIONS_CONTENT_TYPE.ARTIST_BANNER_IMAGE,
        OPTIONS_CONTENT_TYPE.ARTIST_GALLERY_IMAGE,
        OPTIONS_CONTENT_TYPE.ARTIST_MERCHANDISE,
        OPTIONS_CONTENT_TYPE.NOW_PLAYING,
        OPTIONS_CONTENT_TYPE.ARTIST_VIDEO_VIDEO,
        OPTIONS_CONTENT_TYPE.ARTIST_VIDEO_TITLE,
        OPTIONS_CONTENT_TYPE.ARTIST_VIDEO_AUDIO,
        OPTIONS_CONTENT_TYPE.POLL_QUESTION_TEXT,
        OPTIONS_CONTENT_TYPE.POLL_OPTIONS_TEXT,
        OPTIONS_CONTENT_TYPE.RESPONSE_TEXT,
        OPTIONS_CONTENT_TYPE.QUESTION_TEXT,
      ];
    default:
      return [];
  }
};

const getUriInputComponentConfig = (contentType, contentTypeOptions) => {
  const config = contentTypeOptions[contentType];

  return config || contentTypeOptions.UNKNOWN;
};

export const getUriInputComponentErrorMessageKeyOverride = associatedContentResponseValues => {
  if (!associatedContentResponseValues) {
    return '';
  }

  if (associatedContentResponseValues.requestError) {
    return ASSOCIATED_CONTENT_ERRORS.GENERIC_ERROR;
  }

  if (!associatedContentResponseValues.contentExists) {
    return ASSOCIATED_CONTENT_ERRORS.NON_EXISTENT_CONTENT;
  }

  if (associatedContentResponseValues.associatedContentMissing) {
    return ASSOCIATED_CONTENT_ERRORS.NON_EXISTENT_ASSOCIATED_CONTENT;
  }

  return '';
};

const isImageChooserRequired = (contentType, uris) => {
  if (!uris || !contentType) {
    return false;
  }

  const isArtistImageType = ASSOCIATED_CONTENT_IMAGE_TYPES.has(contentType);
  const areArtistUris = uris && isValidSpotifyUriList(uris, OPTIONS_CONTENT_TYPE.ARTIST_BANNER_IMAGE);

  return isArtistImageType && areArtistUris;
};

const shouldRequestAssociatedContent = (fieldChange, contentType) => {
  if (!fieldChange.valid || !fieldChange.value) {
    return false;
  }

  return ASSOCIATED_CONTENT_TYPES.has(contentType);
};

// The payload functions will be used to render in the future, so we need to make sure this is kept in sync
/* eslint-disable @typescript-eslint/no-unused-vars */
export const getPayloadFields = (fieldValue, _market) => [
  CONTENT_DETAILS.fields.CLAIM_TYPE,
  CONTENT_DETAILS.fields.REPORTED_CONTENT_TYPE,
  CONTENT_DETAILS.fields.REPORTED_CONTENT_DESCRIPTION,
  CONTENT_DETAILS.fields.ENTITIES,
];

const TRANSLATION_PREFIX = 'infringement_claim_form';

/* eslint-enable */

class Step2ReportedContent extends PureComponent {
  static propTypes = {
    t: PropTypes.func.isRequired,
    traw: PropTypes.func.isRequired,
    goToNextStep: PropTypes.func.isRequired,
    handleFieldChange: PropTypes.func.isRequired,
    handleBackButton: PropTypes.func.isRequired,
    formData: PropTypes.object.isRequired,
    forceValidation: PropTypes.bool.isRequired,
    renderErrorMessage: PropTypes.func.isRequired,
    preFilledContentUri: PropTypes.string,
    fetchAssociatedContent: PropTypes.func.isRequired,
    resetAssociatedContent: PropTypes.func.isRequired,
    associatedContentResponseValues: PropTypes.object,
    platform: PropTypes.string,
    context: PropTypes.object.isRequired,
    enabledFeatureFlags: PropTypes.instanceOf(Set).isRequired,
    checkFormHasInvalidFields: PropTypes.func.isRequired,
    uriToImages: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.state = {
      uriFieldVisibility: visibility.hidden,
      nonExistentEntities: [],
      hasValidUris: false,
      shouldShowNonExistentEntityDialog: false,
      loadingResolvedUris: false,
      failedToResolveUris: false,
      shouldShowUriNotIncludedDialog: false,
    };

    this.onClaimTypeFieldChange = this.onClaimTypeFieldChange.bind(this);
    this.onContentTypeFieldChange = this.onContentTypeFieldChange.bind(this);
    this.onUriFieldChange = this.onUriFieldChange.bind(this);
    this.getURIComponent = this.getURIComponent.bind(this);
    this.getUriForChangedContentType = this.getUriForChangedContentType.bind(this);
  }

  componentDidMount() {
    analytics.trackShowStepEvent(ShowStepEventLabel.Step2ReportType);
    window.scrollTo(0, 0);
  }

  onClaimTypeFieldChange = (handleFieldChange, onContentTypeFieldChange) => fieldChange => {
    // Update claim type in formData
    handleFieldChange(fieldChange);

    // Since content type options vary based on claim type, reset that as well
    onContentTypeFieldChange({
      fieldIdentifier: CONTENT_DETAILS.fields.REPORTED_CONTENT_TYPE,
      value: '',
      valid: true,
    });
  };

  onContentTypeFieldChange = (handleFieldChange, getUriForChangedContentType, onUriFieldChange) => fieldChange => {
    // Handle the content type change in formData
    handleFieldChange(fieldChange);
    // Since the reported URI *might* vary based on the content type, reset that as well
    onUriFieldChange({
      fieldIdentifier: CONTENT_DETAILS.fields.REPORTED_CONTENT_URIS,
      value: getUriForChangedContentType(fieldChange.value),
      valid: true,
    });
  };

  onUriFieldChange =
    (handleFieldChange, fetchAssociatedContent, resetAssociatedContent, reportedContentTypeValue) => fieldChange => {
      const fieldValue = getFieldValue(this.props.formData);
      const oldSelectedImagesSet = fieldValue(CONTENT_DETAILS.fields.ASSOCIATED_CONTENT);

      // Trigger clean up of any associated content state
      resetAssociatedContent(CONTENT_DETAILS.fields.ASSOCIATED_CONTENT);
      resetAssociatedContent(CONTENT_DETAILS.fields.ENTITIES);
      handleFieldChange(fieldChange);

      if (shouldRequestAssociatedContent(fieldChange, reportedContentTypeValue)) {
        fetchAssociatedContent(fieldChange, reportedContentTypeValue).then(() => {
          const { associatedContentResponseValues } = this.props;

          if (associatedContentResponseValues && oldSelectedImagesSet) {
            const newSelectedImages = new Set();
            associatedContentResponseValues.associatedContentUrls.forEach(image => {
              if (oldSelectedImagesSet.has(image)) {
                newSelectedImages.add(image);
              }
            });

            if (newSelectedImages.size > 0) {
              handleFieldChange({
                fieldIdentifier: CONTENT_DETAILS.fields.ASSOCIATED_CONTENT,
                value: newSelectedImages,
                valid: true,
              });
            }
          }
        });
      }
      if (this.state.uriFieldVisibility === visibility.pristine) {
        this.setState({ uriFieldVisibility: visibility.touched });
      }
    };

  getEntitiesWithAssets = () => {
    const { uriToImages } = this.props;
    const fieldValue = getFieldValue(this.props.formData);

    const images = fieldValue(CONTENT_DETAILS.fields.ASSOCIATED_CONTENT);

    const entities = [];
    for (const uri of Object.keys(uriToImages)) {
      for (const image of images) {
        if (uriToImages[uri].indexOf(image) !== -1) {
          entities.push({ entityId: uri, assetId: image });
        }
      }
    }

    return entities;
  };

  getUrisWithNoImagesSelected = () => {
    const { uriToImages } = this.props;
    const fieldValue = getFieldValue(this.props.formData);

    const images = fieldValue(CONTENT_DETAILS.fields.ASSOCIATED_CONTENT);

    const urisWithNoImagesSelected = [];
    for (const uri of Object.keys(uriToImages)) {
      let exists = false;
      for (const image of images) {
        if (uriToImages[uri].indexOf(image) !== -1) {
          exists = true;
          break;
        }
      }

      if (!exists) {
        urisWithNoImagesSelected.push(uri);
      }
    }

    return urisWithNoImagesSelected;
  };

  removeUrisAndGoToNextStep(requiredFields, onUriFieldChange, urisToRemove) {
    const { goToNextStep } = this.props;

    this.removeEntities(urisToRemove, requiredFields, onUriFieldChange);
    goToNextStep(requiredFields);
  }

  removeEntities = (urisToRemove, requiredFields, onUriFieldChange) => {
    const { formData, handleFieldChange } = this.props;

    if (urisToRemove.length > 0) {
      const fieldValue = getFieldValue(formData);

      const uriTextValue = fieldValue(CONTENT_DETAILS.fields.REPORTED_CONTENT_URIS);
      const uris = getValidSpotifyUriList(buildProductUrisList(uriTextValue));

      const urisWithNonExistentRemoved = uris.filter(uri => urisToRemove.indexOf(uri) === -1);

      const updatedFieldChange = {
        fieldIdentifier: CONTENT_DETAILS.fields.REPORTED_CONTENT_URIS,
        value: urisWithNonExistentRemoved.join('\n'),
        valid: true,
      };
      handleFieldChange(updatedFieldChange);
      onUriFieldChange(updatedFieldChange);

      this.updateEntitiesAttribute(handleFieldChange, fieldValue, urisWithNonExistentRemoved);
    }
  };

  getFailedRequestDialog = () => {
    const { t } = this.props;

    return (
      <Backdrop center>
        <DialogAlert
          colorSet="invertedLight"
          body={
            <div>
              <Type as="p">{t('resolve_uri_api_failure_message')}</Type>
            </div>
          }
          footer={
            <div>
              <ButtonTertiary
                size="small"
                onClick={() => {
                  this.setState({ failedToResolveUris: false });
                }}
              >
                {t('close')}
              </ButtonTertiary>
            </div>
          }
        />
      </Backdrop>
    );
  };

  getInvalidEntityDialog = (requiredFields, onUriFieldChange) => {
    const { t } = this.props;
    const { nonExistentEntities, hasValidUris } = this.state;
    const nonExistentUris = nonExistentEntities.map(entity => entity.uri);

    return (
      <Backdrop center>
        <DialogAlert
          colorSet="invertedLight"
          dialogTitle={`${t('resolve_uri_not_found_dialog')}:`}
          body={
            <div>
              <Type as="p">{nonExistentUris.map(uri => `${uri}\n`)}</Type>
              <br />
              <Type as="p">
                {`${t('reason')}:`}
                <br />
                {t('not_recognized')}
              </Type>
            </div>
          }
          footer={
            <div>
              <ButtonTertiary
                condensed
                size="small"
                onClick={() => {
                  this.setState({ shouldShowNonExistentEntityDialog: false });
                }}
                id="close-unresolved-uri-dialog"
              >
                {t('back_button_label')}
              </ButtonTertiary>
              <ButtonPrimary
                size="small"
                disabled={!hasValidUris}
                onClick={() => {
                  this.removeUrisAndGoToNextStep(requiredFields, onUriFieldChange, nonExistentUris);
                }}
                id="continue-without"
              >
                {t('resolve_uri_not_found_dialog_continue_without')}
              </ButtonPrimary>
            </div>
          }
          id="dialogConfirmation"
        />
      </Backdrop>
    );
  };

  getUrisNotIncludedDialog = (requiredFields, onUriFieldChange) => {
    const { t } = this.props;
    const urisWithImagesNotSelected = this.getUrisWithNoImagesSelected();

    return (
      <Backdrop center>
        <DialogAlert
          colorSet="invertedLight"
          dialogTitle={`${t('resolve_uri_not_found_dialog')}:`}
          body={
            <div>
              <Type as="p">{urisWithImagesNotSelected.map(uri => `${uri}\n`)}</Type>
              <br />
              <Type as="p">
                {`${t('reason')}:`}
                <br />
                {t('no_content_selected')}
              </Type>
            </div>
          }
          footer={
            <div>
              <ButtonTertiary
                condensed
                size="small"
                onClick={() => {
                  this.setState({ shouldShowUriNotIncludedDialog: false });
                }}
                id="close-unresolved-uri-dialog"
              >
                {t('back_button_label')}
              </ButtonTertiary>
              <ButtonPrimary
                size="small"
                onClick={() => {
                  this.removeUrisAndGoToNextStep(requiredFields, onUriFieldChange, urisWithImagesNotSelected);
                }}
                id="continue-without"
              >
                {t('resolve_uri_not_found_dialog_continue_without')}
              </ButtonPrimary>
            </div>
          }
          id="dialogConfirmation"
        />
      </Backdrop>
    );
  };

  async validateFormFieldsAndSubmit(requiredFields) {
    const { goToNextStep, checkFormHasInvalidFields, handleFieldChange } = this.props;

    if (checkFormHasInvalidFields(requiredFields)) {
      return;
    }

    this.setState({ loadingResolvedUris: true });

    const fieldValue = getFieldValue(this.props.formData);
    const uriTextValue = fieldValue(CONTENT_DETAILS.fields.REPORTED_CONTENT_URIS);

    const formattedUris = buildProductUrisList(uriTextValue).map(convertPlaylistV1ToV2);
    const uris = getValidSpotifyUriList(formattedUris);

    const { resolvedUris: resolvedEntities, requestError } = await fetchResolveUris(uris);

    if (requestError) {
      this.setState({ failedToResolveUris: true, loadingResolvedUris: false });
    }

    if (resolvedEntities) {
      const nonExistentEntities = resolvedEntities.filter(entity => !entity.entityExists);

      const hasValidUris = uris.length > nonExistentEntities.length;
      const hasInvalidUris = nonExistentEntities.length > 0;

      const shouldShowNonExistentEntityDialog = hasInvalidUris;
      let shouldShowUriNotIncludedDialog = false;

      this.updateEntitiesAttribute(
        handleFieldChange,
        fieldValue,
        resolvedEntities.map(entity => entity.uri),
      );

      const reportedContentTypeValue = fieldValue(CONTENT_DETAILS.fields.REPORTED_CONTENT_TYPE);

      if (isImageChooserRequired(reportedContentTypeValue, uriTextValue)) {
        if (!shouldShowNonExistentEntityDialog) {
          const urisWithImagesNotSelected = this.getUrisWithNoImagesSelected();

          if (urisWithImagesNotSelected.length > 0) {
            shouldShowUriNotIncludedDialog = true;
          }
        }
      }

      this.setState({
        nonExistentEntities,
        shouldShowNonExistentEntityDialog,
        shouldShowUriNotIncludedDialog,
        hasValidUris,
        failedToResolveUris: false,
        loadingResolvedUris: false,
      });

      if (hasValidUris && !hasInvalidUris && !shouldShowNonExistentEntityDialog && !shouldShowUriNotIncludedDialog) {
        goToNextStep(requiredFields);
      }
    }
  }

  updateEntitiesAttribute(handleFieldChange, fieldValue, uris) {
    const reportedContentTypeValue = fieldValue(CONTENT_DETAILS.fields.REPORTED_CONTENT_TYPE);

    let entities = uris.map(entityId => ({ entityId }));
    if (isImageChooserRequired(reportedContentTypeValue, uris.join(','))) {
      entities = this.getEntitiesWithAssets();
    }

    handleFieldChange({
      fieldIdentifier: CONTENT_DETAILS.fields.ENTITIES,
      value: entities,
      valid: true,
    });
  }

  getURIComponent = (formData, handleFieldChange, forceValidation, associatedContentResponseValues) => {
    const getFormFieldValue = getFieldValue(formData);
    const contentType = getFormFieldValue(CONTENT_DETAILS.fields.REPORTED_CONTENT_TYPE);
    const claimType = getFormFieldValue(CONTENT_DETAILS.fields.CLAIM_TYPE);

    if (contentType) {
      const commonProps = {
        field: {
          name: CONTENT_DETAILS.fields.REPORTED_CONTENT_URIS,
          type: 'text',
          required: true,
          maxLength: 4000,
        },
        value: getFormFieldValue(CONTENT_DETAILS.fields.REPORTED_CONTENT_URIS),
        valid: getFieldValid(formData)(CONTENT_DETAILS.fields.REPORTED_CONTENT_URIS),
        onFieldChange: handleFieldChange,
        forceValidation: forceValidation,
        validateOnChange: true,
        usePopOver: true,
      };

      const { component: UriInputComponent, ...typeSpecificProps } = getUriInputComponentConfig(
        contentType,
        getClaimTypeURIComponent(claimType, this.props.traw),
      );

      const errorMessageKeyOverride = getUriInputComponentErrorMessageKeyOverride(associatedContentResponseValues);

      if (errorMessageKeyOverride) {
        typeSpecificProps.errorMessageKey = withFullTranslationKey(errorMessageKeyOverride);
        // This is needed to make sure we get the error message on first render
        commonProps.forceValidation = true;
      }
      if (this.state.uriFieldVisibility === visibility.hidden) {
        this.setState({ uriFieldVisibility: visibility.pristine });
      }
      return <UriInputComponent {...commonProps} {...typeSpecificProps} />;
    }

    return null;
  };

  /**
   * When switching content type, the URI should be set correctly.
   * 1. If the user has changed the URI field, we always reset to be able to trigger validation.
   * 2. If the user hasn't changed the URI field, and a URI query param is provided the user came here from the old abuse form or in-app reporting flow:
   *    - If the URI is valid for the changed content type value, we pre-fill the URI field based on the query param.
   *    - If the URI is invalid for the type value, we reset the field.
   */
  getUriForChangedContentType = (preFilledContentUri, claimType) => contentType => {
    if (!preFilledContentUri || this.state.uriFieldVisibility === visibility.touched) {
      return '';
    }
    const uriInputComponentConfig = getUriInputComponentConfig(
      contentType,
      getClaimTypeURIComponent(claimType, this.props.traw),
    );
    if (!uriInputComponentConfig?.acceptedUriTypes) {
      return '';
    }
    return isValidSpotifyUriList(preFilledContentUri, uriInputComponentConfig.acceptedUriTypes)
      ? preFilledContentUri
      : '';
  };

  render() {
    const {
      handleFieldChange,
      formData,
      forceValidation,
      renderErrorMessage,
      handleBackButton,
      preFilledContentUri,
      t,
      fetchAssociatedContent,
      resetAssociatedContent,
      associatedContentResponseValues,
    } = this.props;

    const {
      shouldShowNonExistentEntityDialog,
      loadingResolvedUris,
      failedToResolveUris,
      shouldShowUriNotIncludedDialog,
    } = this.state;

    const fieldValue = getFieldValue(formData);
    const fieldValid = getFieldValid(formData);

    const claimType = fieldValue(CONTENT_DETAILS.fields.CLAIM_TYPE);

    const requiredFields = [
      CONTENT_DETAILS.fields.CLAIM_TYPE,
      CONTENT_DETAILS.fields.REPORTED_CONTENT_TYPE,
      CONTENT_DETAILS.fields.REPORTED_CONTENT_URIS,
      CONTENT_DETAILS.fields.REPORTED_CONTENT_DESCRIPTION,
    ];

    const reportedContentTypeValue = fieldValue(CONTENT_DETAILS.fields.REPORTED_CONTENT_TYPE);
    const reportedContentUrisValue = fieldValue(CONTENT_DETAILS.fields.REPORTED_CONTENT_URIS);

    const onUriFieldChange = this.onUriFieldChange(
      handleFieldChange,
      fetchAssociatedContent,
      resetAssociatedContent,
      reportedContentTypeValue,
    );

    const getUriForChangedContentType = this.getUriForChangedContentType(preFilledContentUri, claimType);
    const onContentTypeFieldChange = this.onContentTypeFieldChange(
      handleFieldChange,
      getUriForChangedContentType,
      onUriFieldChange,
    );
    const onClaimTypeChange = this.onClaimTypeFieldChange(handleFieldChange, onContentTypeFieldChange);

    const getURIComponent = () =>
      this.getURIComponent(formData, onUriFieldChange, forceValidation, associatedContentResponseValues);

    const getImageChooser = () => {
      if (isImageChooserRequired(reportedContentTypeValue, reportedContentUrisValue)) {
        requiredFields.push(CONTENT_DETAILS.fields.ASSOCIATED_CONTENT);

        // Do not render the image chooser in case there are no e.g. gallery images for the artist.
        if (associatedContentResponseValues && associatedContentResponseValues.associatedContentMissing) {
          return null;
        }

        const images = associatedContentResponseValues ? associatedContentResponseValues.associatedContentUrls : [];

        // Pass any previously selected images to the image chooser
        const previouslySelectedImages = fieldValue(CONTENT_DETAILS.fields.ASSOCIATED_CONTENT)
          ? [...fieldValue(CONTENT_DETAILS.fields.ASSOCIATED_CONTENT)]
          : [];

        const imageChooserProps = {
          field: {
            name: CONTENT_DETAILS.fields.ASSOCIATED_CONTENT,
            required: true,
          },
          valid: fieldValid(CONTENT_DETAILS.fields.ASSOCIATED_CONTENT),
          onFieldChange: handleFieldChange,
          selectedImages: previouslySelectedImages,
          forceValidation: forceValidation,
          label: `${TRANSLATION_PREFIX}:image_chooser_title`,
          errorMessageKey: `${TRANSLATION_PREFIX}:image_chooser_title`,
          t,
          images: images,
          paginationOfKey: `${TRANSLATION_PREFIX}:pagination_of`,
          disclaimer: `${TRANSLATION_PREFIX}:form_associated_content_disclaimer`,
        };

        return <ImageChooser {...imageChooserProps} />;
      }
      return null;
    };

    const getContentDescriptionLabel = () => {
      return claimType === OPTIONS_CLAIM_TYPE.OTHER
        ? CONTENT_DETAILS.labels.REPORTED_CONTENT_DESCRIPTION_NON_IP
        : CONTENT_DETAILS.labels.REPORTED_CONTENT_DESCRIPTION;
    };

    const contentTypeOptions = getContentTypeOptions(claimType);

    return (
      <>
        {shouldShowNonExistentEntityDialog && this.getInvalidEntityDialog(requiredFields, onUriFieldChange)}
        {failedToResolveUris && this.getFailedRequestDialog()}
        {shouldShowUriNotIncludedDialog && this.getUrisNotIncludedDialog(requiredFields, onUriFieldChange)}
        <SelectField
          field={{
            name: CONTENT_DETAILS.fields.CLAIM_TYPE,
            type: 'text',
            options: OPTIONS_CLAIM_TYPE_LIST,
            required: true,
            labelOverride: USE_LABEL_FROM_TRANSLATION_PREFIX,
          }}
          hasDefaultPlaceholder
          value={fieldValue(CONTENT_DETAILS.fields.CLAIM_TYPE)}
          valid={fieldValid(CONTENT_DETAILS.fields.CLAIM_TYPE)}
          tPrefix={withFullTranslationKey(CONTENT_DETAILS.translationPrefixes.CLAIM_TYPE)}
          onFieldChange={onClaimTypeChange}
          forceValidation={forceValidation}
        />

        <SelectField
          field={{
            name: CONTENT_DETAILS.fields.REPORTED_CONTENT_TYPE,
            type: 'text',
            options: contentTypeOptions,
            required: true,
            labelOverride: USE_LABEL_FROM_TRANSLATION_PREFIX,
          }}
          hasDefaultPlaceholder
          value={reportedContentTypeValue}
          valid={fieldValid(CONTENT_DETAILS.fields.REPORTED_CONTENT_TYPE)}
          tPrefix={withFullTranslationKey(CONTENT_DETAILS.translationPrefixes.REPORTED_CONTENT_TYPE)}
          onFieldChange={onContentTypeFieldChange}
          forceValidation={forceValidation}
        />

        {getURIComponent()}
        {getImageChooser()}

        <TextareaField
          field={{
            name: CONTENT_DETAILS.fields.REPORTED_CONTENT_DESCRIPTION,
            type: 'text',
            required: true,
            maxlength: 2000,
            labelOverride: t(getContentDescriptionLabel()),
          }}
          value={fieldValue(CONTENT_DETAILS.fields.REPORTED_CONTENT_DESCRIPTION)}
          valid={fieldValid(CONTENT_DETAILS.fields.REPORTED_CONTENT_DESCRIPTION)}
          onFieldChange={handleFieldChange}
          forceValidation={forceValidation}
          errorMessageKey={`${TRANSLATION_PREFIX}:field_required`}
        />

        {renderErrorMessage()}
        <br />
        <div style={{ display: 'flex' }}>
          <BackButton handleBackButton={handleBackButton} />
          <ButtonPrimary
            id="go-to-next-step"
            onClick={async () => await this.validateFormFieldsAndSubmit(requiredFields, onUriFieldChange)}
            disabled={loadingResolvedUris}
          >
            {t('continue')}
          </ButtonPrimary>
        </div>
      </>
    );
  }
}

export default withContextualInfo(withTranslation('infringement_claim_form')(Step2ReportedContent));
